'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { CheckCircle2, ArrowDown, TrendingUp, Wallet, Shield, Lock, Zap, Network, Bell } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Feature {
  icon: any;
  title: string;
  description: string;
}

interface FeaturesTwoColumnSectionProps {
  translationNamespace: string;
  features: Feature[];
  ctaHref?: string;
}

const iconMap: Record<string, any> = {
  wallet: Wallet,
  shield: Shield,
  lock: Lock,
  zap: Zap,
  network: Network,
  bell: Bell,
  check: CheckCircle2,
  arrow: ArrowDown,
  trend: TrendingUp,
};

export function FeaturesTwoColumnSection({
  translationNamespace,
  features,
  ctaHref = '/contact'
}: FeaturesTwoColumnSectionProps) {
  const t = useTranslations(translationNamespace);
  const sectionRef = useScrollAnimation({ threshold: 0.1 });

  // Map features to step cards
  const steps = [
    {
      number: '01',
      icon: iconMap.wallet || CheckCircle2,
      title: t('step1.title'),
      description: t('step1.description'),
    },
    {
      number: '02',
      icon: iconMap.shield || ArrowDown,
      title: t('step2.title'),
      description: t('step2.description'),
    },
    {
      number: '03',
      icon: iconMap.lock || TrendingUp,
      title: t('step3.title'),
      description: t('step3.description'),
    },
    {
      number: '04',
      icon: iconMap.zap || CheckCircle2,
      title: t('step4.title'),
      description: t('step4.description'),
    },
    {
      number: '05',
      icon: iconMap.network || ArrowDown,
      title: t('step5.title'),
      description: t('step5.description'),
    },
    {
      number: '06',
      icon: iconMap.bell || TrendingUp,
      title: t('step6.title'),
      description: t('step6.description'),
    },
  ].filter(step => step.title && step.title !== `step${step.number.slice(1)}.title`);

  return (
    <SectionWrapper variant="gradient" padding="md" ref={sectionRef.ref} className="relative bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-900">
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 dark:bg-primary-500/5 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 dark:bg-blue-500/5 blur-3xl" />
      </div>

      <div className="container relative z-10">
        {/* Section Heading */}
        <SectionHeading
          page=""
          fallbackPill={t('pill')}
          fallbackTitle={t('title')}
          align="center"
          maxWidthClass="max-w-4xl"
          className="mb-4"
        />

        {/* Subtitle */}
        <Typography variant="p" className="text-center mb-12 text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg">
          {t('subtitle')}
        </Typography>

        {/* Three Step Cards */}
        <div className="container">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === steps.length - 1;

              return (
                <div key={index} className="relative h-full flex">
                  {/* Card */}
                  <div
                    className={cn(
                      "relative bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-blue-500/30 transition-all duration-700 group hover:border-blue-500 dark:hover:border-blue-500/60 hover:shadow-lg hover:shadow-blue-500/10 dark:hover:shadow-blue-500/10 w-full flex flex-col",
                      sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    )}
                    style={{ transitionDelay: `${200 + index * 150}ms` }}
                  >
                    {/* Number Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {step.number}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="mb-6">
                      <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center border border-blue-200 dark:border-blue-500/30">
                        <Icon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>

                    {/* Title */}
                    <Typography variant="h3" className="mb-4 text-gray-900 dark:text-white font-bold">
                      {step.title}
                    </Typography>

                    {/* Description */}
                    <Typography variant="p" className="text-gray-600 dark:text-gray-300 leading-relaxed flex-grow">
                      {step.description}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
